export default {
  namespaced: true,

  state: {
    messages: [],
    groupMembers: [],
    mutedUsers: [],
    mutedUsersLoaded: false,
    currentUserMuted: false,
    groupNotice: '',
    productCardInfo: null,
    liveStatus: {
      connected: false,
      groupJoined: false,
      loading: false,
      error: null
    },
    likeCount: 0, // 点赞数统计
    viewCount: 0, // 浏览量PV统计
    onlineUsers: [],
    onlineUserCount: 0,
    isBigGroup: false,
    roomGoodsTotal: 0,
    microphoneVolume: 1.0, // 麦克风音量，范围 0-1
    isMirrorEnabled: false, // 是否启用前置摄像头镜像
    roomInfo: {
      groupMuted: false,
      anchorName: '',
      anchorAvatar: ''
    }
  },

  getters: {
    getAllMessages: state => state.messages,

    getVisibleMessagesFn: state => (maxCount = 50) => {
      const total = state.messages.length
      if (total <= maxCount) {
        return state.messages
      }
      return state.messages.slice(total - maxCount, total)
    },

    getGroupMembers: state => state.groupMembers,

    getMutedUsers: state => state.mutedUsers,

    getMutedUsersLoaded: state => state.mutedUsersLoaded,

    isUserMutedFn: state => userId => {
      return state.mutedUsers.some(user => user.userId === userId)
    },

    getOnlineUsers: state => {
      return state.onlineUsers.map(user => ({
        userId: user.userId || '',
        nickName: user.nickName || user.nickname || '用户',
        nickname: user.nickname || user.nickName || '用户',
        userAvatar: user.userAvatar || user.avatar || '',
        avatar: user.avatar || user.userAvatar || '',
        role: user.role || 'user'
      }))
    },

    getOnlineUserCount: state => state.onlineUserCount,
    getViewCount: state => state.viewCount,
    getIsBigGroup: state => state.isBigGroup,

    getChatStats: state => {
      const totalMessages = state.messages.length
      const userMessages = state.messages.filter(msg => !msg.isSystem).length
      const systemMessages = state.messages.filter(msg => msg.isSystem).length
      const onlineCount = state.onlineUserCount
      const likeCount = state.likeCount
      const viewCount = state.viewCount
      return {
        totalMessages,
        userMessages,
        systemMessages,
        onlineCount,
        likeCount,
        viewCount
      }
    },
    getMicrophoneVolume: state => state.microphoneVolume,
    getMirrorEnabled: state => state.isMirrorEnabled
  },

  mutations: {
    // 在线用户相关
    SET_ONLINE_USERS(state, users) {
      state.onlineUsers = users.map(user => ({
        userId: user.userId || '',
        nickName: user.nickName || user.nickname || '用户',
        nickname: user.nickname || user.nickName || '用户',
        userAvatar: user.userAvatar || user.avatar || '',
        avatar: user.avatar || user.userAvatar || '',
        role: user.role || 'user'
      }))
    },
    SET_ONLINE_USER_COUNT(state, count) {
      state.onlineUserCount = count
    },
    SET_VIEW_COUNT(state, count) {
      state.viewCount = count
    },
    SET_IS_BIG_GROUP(state, isBigGroup) {
      state.isBigGroup = isBigGroup
    },
    ADD_ONLINE_USER(state, user) {
      if (!state.onlineUsers.some(u => u.userId === user.userId)) {
        state.onlineUsers.push({
          userId: user.userId || '',
          nickName: user.nickName || user.nickname || '用户',
          nickname: user.nickname || user.nickName || '用户',
          userAvatar: user.userAvatar || user.avatar || '',
          avatar: user.avatar || user.userAvatar || '',
          role: user.role || 'user'
        })
      }
    },
    REMOVE_ONLINE_USER(state, userId) {
      state.onlineUsers = state.onlineUsers.filter(u => u.userId !== userId)
    },

    // 消息相关
    SET_MESSAGES(state, messages) {
      state.messages = messages
    },
    ADD_MESSAGE(state, message) {
      if (state.messages.length >= 1000) {
        state.messages = state.messages.slice(-500)
      }
      state.messages.push(message)
    },
    ADD_MESSAGES(state, messages) {
      state.messages.push(...messages)
    },
    UPDATE_MESSAGE(state, { messageId, updates }) {
      const index = state.messages.findIndex(msg => msg.messageId === messageId)
      if (index !== -1) {
        state.messages[index] = { ...state.messages[index], ...updates }
      }
    },

    // 群组成员相关
    SET_GROUP_MEMBERS(state, members) {
      state.groupMembers = members.map(member => ({
        userId: member.userId || '',
        nickName: member.nickName || member.nickname || '用户',
        nickname: member.nickname || member.nickName || '用户',
        userAvatar: member.userAvatar || member.avatar || '',
        avatar: member.avatar || member.userAvatar || '',
        role: member.role || 'user'
      }))
    },
    UPDATE_GROUP_MEMBER(state, member) {
      const index = state.groupMembers.findIndex(
        m => m.userId === member.userId
      )
      const normalizedMember = {
        userId: member.userId || '',
        nickName: member.nickName || member.nickname || '用户',
        nickname: member.nickname || member.nickName || '用户',
        userAvatar: member.userAvatar || member.avatar || '',
        avatar: member.avatar || member.userAvatar || '',
        role: member.role || 'user'
      }
      if (index !== -1) {
        state.groupMembers[index] = normalizedMember
      } else {
        state.groupMembers.push(normalizedMember)
      }
    },
    REMOVE_GROUP_MEMBER(state, userId) {
      state.groupMembers = state.groupMembers.filter(m => m.userId !== userId)
    },

    // 禁言相关
    SET_MUTED_USERS(state, users) {
      state.mutedUsers = users
      state.mutedUsersLoaded = true
    },
    ADD_MUTED_USER(state, user) {
      if (!state.mutedUsers.some(u => u.userId === user.userId)) {
        state.mutedUsers.push(user)
      }
      state.mutedUsersLoaded = true
    },
    REMOVE_MUTED_USER(state, userId) {
      state.mutedUsers = state.mutedUsers.filter(u => u.userId !== userId)
      state.mutedUsersLoaded = true
    },
    SET_CURRENT_USER_MUTED(state, isMuted) {
      state.currentUserMuted = isMuted
    },

    // 公告和产品卡片
    SET_GROUP_NOTICE(state, notice) {
      state.groupNotice = notice
    },
    SET_PRODUCT_CARD_INFO(state, info) {
      state.productCardInfo = info
    },

    // IM状态
    SET_IM_STATUS(state, status) {
      state.liveStatus = { ...state.liveStatus, ...status }
    },

    // 点赞相关
    SET_LIKE_COUNT(state, count) {
      state.likeCount = count
    },
    INCREMENT_LIKE_COUNT(state) {
      state.likeCount++
    },

    // 清空所有数据
    CLEAR_LIVE_DATA(state) {
      state.messages = []
      state.groupMembers = []
      state.mutedUsers = []
      state.mutedUsersLoaded = false
      state.currentUserMuted = false
      state.groupNotice = ''
      state.productCardInfo = null
      state.liveStatus = {
        connected: false,
        groupJoined: false,
        loading: false,
        error: null
      }
      state.likeCount = 0
      state.onlineUsers = []
      state.onlineUserCount = 0
      state.isBigGroup = false
    },
    // 房间商品相关
    SET_ROOM_GOODS_TOTAL(state, total) {
      state.roomGoodsTotal = total
    },
    SET_MICROPHONE_VOLUME(state, volume) {
      state.microphoneVolume = volume
    },
    SET_MIRROR_ENABLED(state, enabled) {
      state.isMirrorEnabled = enabled
    },
    SET_ROOM_INFO(state, roomInfo) {
      state.roomInfo = { ...state.roomInfo, ...roomInfo }
    }
  },

  actions: {
    addMessage({ commit }, message) {
      commit('ADD_MESSAGE', message)
    },
    addMessages({ commit }, messages) {
      commit('ADD_MESSAGES', messages)
    },
    updateGroupMembers({ commit }, members) {
      commit('SET_GROUP_MEMBERS', members)
    },
    updateMutedUsers({ commit }, users) {
      commit('SET_MUTED_USERS', users)
    },
    muteUser({ commit }, user) {
      commit('ADD_MUTED_USER', user)
    },
    unmuteUser({ commit }, userId) {
      commit('REMOVE_MUTED_USER', userId)
    },
    updateIMStatus({ commit }, status) {
      commit('SET_IM_STATUS', status)
    },
    clearLiveData({ commit }) {
      commit('CLEAR_LIVE_DATA')
    }
  }
}
